import React, { useContext } from 'react';

import { Badge } from 'antd';

import { getMoment } from '@libs/NwMoment';
import { getNow, showDateNoTime } from '@libs/NwMoment';

import { ListContext } from '@contexts/ListContext';

import { useRemoveLegal } from '@hooks/API/Contact/UseContactLegals';
import useNwBreakPoints from '@hooks/Layout/UseNwBreakPoints';

import CountryLabel from '@components/Gui/CountryLabel';
import { NwCard } from '@components/Gui/NwCard/NwCard';
import NwDivider from '@components/Gui/NwDivider';
import NwDropDownMenu from '@components/Gui/NwDropDownMenu';
import NwEllipsis from '@components/Gui/NwEllipsis';
import NwIcon from '@components/Gui/NwIcon';
import StyleVariables from '@components/Gui/StyleVariables';

import { Wrapper, StyledTag, LegalDetails } from './LegalItemUI';
import LegalUpload from './LegalUpload';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const LegalItem = ({ document, contactId, readOnly, onEditDoc }) => {
    const { mutateAsync: removeLegal } = useRemoveLegal();
    const diffDays = document.ExpirationDate && getNow(true).diff(getMoment(document.ExpirationDate), 'days');
    const breakpoints = useNwBreakPoints();
    const { cachedList } = useContext(ListContext);
    const legalTypes = cachedList.legalTypes.items;
    const documentName = legalTypes.find((el) => el.Code.toLowerCase() === document.Type.toLowerCase()).Name;

    const deleteDocument = () => {
        removeLegal({ LegalID: document.ID, ContactID: contactId });
    };

    const renderActions = () => (
        <NwDropDownMenu
            size="small"
            actions={[
                { name: '', key: 'edit', pinned: true, icon: byPrefixAndName.fasl['pen'], action: onEditDoc },
                {
                    name: 'delete',
                    type: 'delete',
                    key: 'delete',
                    icon: byPrefixAndName.fasl['trash-alt'],
                    confirmationPlacement: 'left',
                    action: deleteDocument,
                },
            ]}
        />
    );

    const renderTypeIcon = () => {
        switch (document.Type) {
            case 'VISA':
                return byPrefixAndName.fass['plane'];
            case 'ID':
                return byPrefixAndName.fass['id-card'];
            case 'PASSPORT':
                return byPrefixAndName.fass['passport'];
            case 'SSN':
                return byPrefixAndName.fass['id-badge'];
            case 'DRIVINGLIC':
                return byPrefixAndName.fass['car'];
            case 'CONTRACT':
                return byPrefixAndName.fass['file-contract'];
            default:
                return byPrefixAndName.fass['file'];
        }
    };

    const invalid = diffDays !== null && (diffDays >= 0 || (diffDays < 0 && diffDays >= -30));

    const LegalDocumentTitle = () => {
        return (
            <>
                {diffDays !== null && (
                    <>
                        {diffDays >= 0 && 'expired'}
                        {diffDays < 0 && diffDays >= -30 && 'expiring'}
                    </>
                )}
                &nbsp;
                <NwIcon icon={renderTypeIcon()} />
            </>
        );
    };

    const getCardRibbonColor = () => {
        if (diffDays !== null) {
            if (diffDays >= 0) return StyleVariables.DangerColor;
            else if (diffDays < 0 && diffDays >= -30) {
                return StyleVariables.GoldColor;
            }
        }
        return StyleVariables.PrimaryColor;
    };

    const renderCard = () => (
        <NwCard
            title={
                <div className="legal-card-title-wrapper">
                    {breakpoints === 1 && <StyledTag color={getCardRibbonColor()}>{LegalDocumentTitle()}</StyledTag>}
                    <h1 className={`legal-card-title ${invalid ? ' invalid' : ''}`}>
                        {documentName}
                        {document.YearRef ? ` (${document.YearRef})` : ''}
                    </h1>
                </div>
            }
            extra={!readOnly && renderActions()}
        >
            <div className="legal-details-content">
                <LegalDetails label="Number" value={document.DocNumber} />
                <LegalDetails label="Country" value={<CountryLabel code={document.CountryCode} flag={true} />} />
                <LegalDetails label="Issued on" value={document.StartDate && showDateNoTime(document.StartDate)} />
                <LegalDetails
                    label="Will expire on"
                    value={document.ExpirationDate && showDateNoTime(document.ExpirationDate)}
                />
                <LegalDetails label="Details" value={<NwEllipsis popover text={document.Details} rows={4} />} />
                {!readOnly && (
                    <>
                        <NwDivider>Scan</NwDivider>
                        <LegalUpload item={document} />
                    </>
                )}
            </div>
        </NwCard>
    );

    // Su mobile lo stato del documento e' gia' nel tag dentro il titolo: il ribbon sarebbe un duplicato
    return (
        <Wrapper>
            {breakpoints === 1 ? (
                renderCard()
            ) : (
                <Badge.Ribbon color={getCardRibbonColor()} placement="start" text={LegalDocumentTitle()}>
                    {renderCard()}
                </Badge.Ribbon>
            )}
        </Wrapper>
    );
};

export default LegalItem;
